<template>
  <div :class="{ fixed }">
    <div
      v-for="group in groupedTemplates"
      :key="group.key"
      class="template-group"
    >
      <h6
        class="template-group__header"
        @click="toggleGroup(group.key)"
      >
        <i
          :class="{ 'fa-folder-open': groupsVisibility[group.key] }"
          class="fa fa-folder"
        />&nbsp;
        {{ group.name }}
        <i
          v-if="group.description.length > 0"
          v-b-tooltip.hover
          :title="group.description"
          class="fa fa-question-circle"
        />
        ({{ Object.keys(group.items).length }})
      </h6>
      <table
        v-show="groupsVisibility[group.key]"
        class="table table-striped"
      >
        <tbody>
          <tr
            v-for="(tpl, tplKey) in group.items"
            :key="tplKey"
          >
            <td>
              <div class="template__header">
                <span
                  :class="{ 'bold': templatesVisibility[tplKey] }"
                  @click="toggle(tplKey)"
                >
                  <i
                    :class="{
                      'fa-plus-square-o': !templatesVisibility[tplKey],
                      'fa-minus-square-o': templatesVisibility[tplKey]
                    }"
                    class="fa"
                  />
                  &nbsp;{{ tplKey }} <span
                    :class="{ 'badge-warning': tpl.type === 'attribute' }"
                    class="badge badge-info"
                  >{{ tpl.type }}</span>
                </span>
              </div>
              <div
                v-show="templatesVisibility[tplKey]"
                class="template__body"
              >
                {{ tpl.name }}
                <small v-if="tpl.description.length > 0"><br>{{ tpl.description }}</small>
                <div class="template__example">
                  <small>
                    <b>Default:</b><br>
                    <template v-if="tpl.default.length > 0">
                      <ui-template-editor :value="tpl.default" />
                    </template>
                    <template v-else>empty</template>
                  </small>
                </div>
                <div class="template__actions">
                  <button
                    class="btn btn-sm btn-primary"
                    @click.prevent="edit(tplKey)"
                  >
                    <i class="fa fa-pencil-square-o" /> {{ $t('Edit') }}
                  </button>
                  <button
                    class="btn btn-sm btn-danger"
                    @click.prevent="remove(tplKey)"
                  >
                    <i class="fa fa-trash" /> {{ $t('Delete') }}
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <ui-button-float
      v-if="!hideAddButton"
      variant="success"
      @click.prevent="add"
    />
  </div>
</template>

<script>
export default {
  name: 'AppTemplates',
  props: {
    value: {
      type: Object,
      required: true,
    },
    fixed: {
      type: Boolean,
      default: true,
    },
    hideAddButton: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      templates: [],
      templatesVisibility: {},
      groupedTemplates: [],
      groupsVisibility: {},
    };
  },
  watch: {
    value: {
      handler(v) {
        this.templates = Object.keys(v).map(i => ({ key: i, ...v[i] }));
        const templatesVisibility = { ...this.templatesVisibility };
        this.templates.forEach(e => {
          if (typeof templatesVisibility[e.key] !== 'boolean') {
            templatesVisibility[e.key] = false;
          }
        });
        this.templatesVisibility = templatesVisibility;
        const groupedTemplates = new Map();
        const groupsVisibility = { ...this.groupsVisibility };
        if (Object.keys(v).length > 0) {
          Object.keys(v).forEach(key => {
            const tpl = v[key];
            let groupKey = tpl.group;
            if (typeof groupKey === 'undefined' || groupKey === null || groupKey.length === 0) {
              groupKey = 'Main';
            }
            // Set a group visibility
            if (typeof groupsVisibility[groupKey] !== 'boolean') {
              groupsVisibility[groupKey] = false;
            }
            let name = '';
            let description = '';
            if (groupKey.indexOf('-') !== -1) {
              name = groupKey.substring(0, groupKey.indexOf('-'));
              description = groupKey.substring(groupKey.indexOf('-') + 1, groupKey.length);
            } else {
              const groupParts = groupKey.match(/[A-Z][a-z]+/g);
              if (groupParts !== null) {
                name = groupKey.match(/[A-Z][a-z]+/g).join(' ');
              } else {
                name = groupKey;
              }
            }
            let group = {
              key: groupKey, name, description, items: {},
            };
            if (groupedTemplates.has(groupKey)) {
              group = groupedTemplates.get(groupKey);
            }
            group.items[key] = tpl;
            groupedTemplates.set(groupKey, group);
          });
          this.groupedTemplates = Array.from(groupedTemplates.values()).sort((a, b) => {
            if (a.key < b.key) {
              return -1;
            }
            if (a.key > b.key) {
              return 1;
            }
            return 0;
          });
          this.groupsVisibility = groupsVisibility;
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    toggle(key) {
      this.$set(this.templatesVisibility, key, !this.templatesVisibility[key]);
    },
    remove(key) {
      this.$emit('delete', key);
    },
    edit(key) {
      this.$emit('edit', key);
    },
    add() {
      this.$emit('add');
    },
    toggleGroup(groupKey) {
      this.$set(this.groupsVisibility, groupKey, !this.groupsVisibility[groupKey]);
    },
  },
};
</script>

<style lang="scss" scoped>
  @import "~bootstrap-vars";

  table {
    width: 100%;
  }

  .fixed {
    overflow-y: auto;
    max-height: calc(100vh - 300px);
  }

  .template {
    &__header {
      cursor: pointer;
    }
    &__body {
      padding-left: 18px;
    }
    &__actions {
      margin-top: 10px;
    }
    &__example {
      border: $gray-300 1px solid;
      padding: 5px;
      margin-top: 5px;
    }
  }

  .template-group {
    &__header {
      cursor: pointer;
      font-size: .9rem;
    }
  }
</style>
